/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Pricing helper functions for the line item pricing calculator.
 * Handles overhead calculation, margin determination, and formula variations.
 */

import { BUSINESS_RULES } from "@/lib/constants/business/rules";
import { roundCurrency } from "@/lib/utils/domain/calculation-helpers";
import type { BudgetRates } from "./estimates-types";
import type { LineItemPricingResult } from "./estimates-line-item-pricing";
import {
  applyOverheadConstraints,
  safeDivide,
} from "./estimates-calculations-helpers";

export function calculateInitialOverhead(
  adjustedTotalCost: number,
  totalCost: number,
  materialCost: number,
  laborCost: number,
  equipmentCost: number,
  overheadRate: number,
  overheadRecoveryMethod: string | undefined,
  _overheadBasis: "direct" | "revenue",
  budgetRates: BudgetRates | null | undefined,
  estimateSubtotalForOverheadAllocation?: number,
  subcontractorCost: number = 0,
): number {
  if (
    overheadRecoveryMethod === "fixed_amount" &&
    typeof budgetRates?.overheadFixedAmount === "number"
  ) {
    if (
      estimateSubtotalForOverheadAllocation !== undefined &&
      estimateSubtotalForOverheadAllocation > 0
    ) {
      return (
        Math.round(
          budgetRates.overheadFixedAmount *
            (adjustedTotalCost / estimateSubtotalForOverheadAllocation) *
            100,
        ) / 100
      );
    }
    return budgetRates.overheadFixedAmount;
  }

  if (
    overheadRecoveryMethod === "activity_based" &&
    budgetRates?.categoryOverheadRates
  ) {
    const taxRatio = totalCost > 0 ? adjustedTotalCost / totalCost : 1;
    const adjustedMaterialCost = materialCost * taxRatio;
    const adjustedLaborCost = laborCost * taxRatio;
    const adjustedEquipmentCost = equipmentCost * taxRatio;
    const adjustedSubcontractorCost = subcontractorCost * taxRatio;

    const materialsOverhead =
      Math.round(
        adjustedMaterialCost *
          (budgetRates.categoryOverheadRates.materials ?? overheadRate) *
          100,
      ) / 100;
    const laborOverhead =
      Math.round(
        adjustedLaborCost *
          (budgetRates.categoryOverheadRates.labor ?? overheadRate) *
          100,
      ) / 100;
    const equipmentOverhead =
      Math.round(
        adjustedEquipmentCost *
          (budgetRates.categoryOverheadRates.equipment ?? overheadRate) *
          100,
      ) / 100;
    const subcontractorOverhead =
      Math.round(
        adjustedSubcontractorCost *
          (budgetRates.categoryOverheadRates.subcontractors ?? overheadRate) *
          100,
      ) / 100;

    const categorizedTotal =
      adjustedMaterialCost +
      adjustedLaborCost +
      adjustedEquipmentCost +
      adjustedSubcontractorCost;
    const uncategorizedCost = Math.max(0, adjustedTotalCost - categorizedTotal);
    const uncategorizedOverhead = roundCurrency(
      uncategorizedCost * overheadRate,
    );

    return (
      materialsOverhead +
      laborOverhead +
      equipmentOverhead +
      subcontractorOverhead +
      uncategorizedOverhead
    );
  }

  // Single overhead recovery (direct or revenue basis)
  return roundCurrency(adjustedTotalCost * overheadRate);
}

export function determineMarginRate(
  budgetRates: BudgetRates | null | undefined,
): number {
  return budgetRates?.marginRate ?? BUSINESS_RULES.DEFAULT_MARGIN_RATE;
}

export function determineProfitMargin(
  budgetRates: BudgetRates | null | undefined,
): number | undefined {
  return budgetRates?.profitMargin;
}

export function calculateZeroProfitResult(
  unitCost: number,
  totalCost: number,
  baseForMargin: number,
): LineItemPricingResult {
  const totalPrice = baseForMargin;
  const quantity = unitCost > 0 ? totalCost / unitCost : 1;
  const unitPrice = quantity > 0 ? totalPrice / quantity : unitCost;

  return {
    unitPrice,
    totalPrice,
    marginPercentage: 0,
    profitPercentage: 0,
  };
}

export function calculatePricingFromBudgetRates(
  adjustedTotalCost: number,
  totalCost: number,
  overhead: number,
  contingency: number,
  baseForMargin: number,
  overheadRate: number,
  marginRate: number,
  useRevenueFormula: boolean,
  resolvedProfitMargin: number | undefined,
  effectiveContingencyRate: number,
  budgetRates: BudgetRates | null | undefined,
): { totalPrice: number; profitPercentage: number; warning?: string } {
  const effectiveMargin = resolvedProfitMargin ?? marginRate;

  if (effectiveMargin === 0) {
    let totalPrice: number;
    if (useRevenueFormula && overheadRate < 1) {
      const denominator = 1 - overheadRate - effectiveContingencyRate;
      totalPrice =
        denominator > 0
          ? adjustedTotalCost / denominator
          : adjustedTotalCost + overhead + contingency;
    } else {
      totalPrice = adjustedTotalCost + overhead + contingency;
    }
    return { totalPrice, profitPercentage: 0 };
  }

  const result = calculateWithProfitMargin(
    adjustedTotalCost,
    totalCost,
    overhead,
    contingency,
    baseForMargin,
    overheadRate,
    useRevenueFormula,
    effectiveMargin,
    effectiveContingencyRate,
    budgetRates,
  );

  return {
    totalPrice: result.totalPrice,
    profitPercentage: result.profitPercentage,
    warning: result.warning,
  };
}

export function calculateWithProfitMargin(
  adjustedTotalCost: number,
  _totalCost: number,
  overhead: number,
  contingency: number,
  baseForMargin: number,
  overheadRate: number,
  useRevenueFormula: boolean,
  profitMargin: number,
  effectiveContingencyRate: number,
  budgetRates: BudgetRates | null | undefined,
): { totalPrice: number; profitPercentage: number; warning?: string } {
  let totalPrice: number;
  let newOverhead = overhead;
  let newContingency = contingency;
  let rateSumWarning: string | undefined;

  if (useRevenueFormula) {
    const rateSum = profitMargin + overheadRate + effectiveContingencyRate;
    const denominator = 1 - rateSum;
    if (denominator > 0) {
      totalPrice = adjustedTotalCost / denominator;
      newOverhead = applyOverheadConstraints(
        totalPrice * overheadRate,
        budgetRates,
      );
      newContingency = totalPrice * effectiveContingencyRate;
    } else {
      totalPrice = safeDivide(baseForMargin, 1 - profitMargin, baseForMargin);
      rateSumWarning = `Revenue formula rates sum to ${Math.round(rateSum * 100)}% (overhead ${Math.round(overheadRate * 100)}% + margin ${Math.round(profitMargin * 100)}% + contingency ${Math.round(effectiveContingencyRate * 100)}%). Must be under 100%. Fell back to direct cost formula.`;
    }
  } else {
    totalPrice = safeDivide(baseForMargin, 1 - profitMargin, baseForMargin);
  }

  const profit = totalPrice - adjustedTotalCost - newOverhead - newContingency;
  const profitPercentage =
    totalPrice > 0 ? Math.round((profit / totalPrice) * 10000) / 100 : 0;

  const warning =
    rateSumWarning ??
    (profitPercentage < 0
      ? `Negative profit (${profitPercentage}%): Check your overhead and profit margin rates - current configuration results in a loss.`
      : undefined);

  return { totalPrice, profitPercentage, warning };
}

export function calculateWithProfitOverride(
  adjustedTotalCost: number,
  totalCost: number,
  unitCost: number,
  overhead: number,
  contingency: number,
  _overheadRate: number,
  _overheadRecoveryMethod: string | undefined,
  _useRevenueFormula: boolean,
  profitPercentageOverride: number,
  _effectiveContingencyRate: number,
  _budgetRates: BudgetRates | null | undefined,
): LineItemPricingResult {
  const profitRate = profitPercentageOverride / 100;
  const baseForMargin = adjustedTotalCost + overhead + contingency;
  const adjustedTotalPrice =
    profitRate < 1 ? baseForMargin / (1 - profitRate) : baseForMargin;

  const quantity = unitCost > 0 ? totalCost / unitCost : 1;
  let adjustedUnitPrice: number;
  if (quantity > 0) {
    adjustedUnitPrice = adjustedTotalPrice / quantity;
  } else if (unitCost > 0) {
    const singleBase = unitCost * (1 + _overheadRate);
    adjustedUnitPrice =
      profitRate < 1 ? singleBase / (1 - profitRate) : singleBase;
  } else {
    adjustedUnitPrice = unitCost;
  }

  const adjustedMarginPercentage =
    adjustedUnitPrice > unitCost
      ? ((adjustedUnitPrice - unitCost) / adjustedUnitPrice) * 100
      : adjustedTotalPrice > 0
        ? ((adjustedTotalPrice - adjustedTotalCost) / adjustedTotalPrice) * 100
        : 0;

  return {
    unitPrice: adjustedUnitPrice,
    totalPrice: adjustedTotalPrice,
    marginPercentage: adjustedMarginPercentage,
    profitPercentage: profitPercentageOverride,
  };
}
